/**
 * CopyFeeModal Component
 *
 * Modal for copying fee configuration data from a source node to one or more
 * valid destinations. Destinations are drawn from the full taxonomy (market
 * segments and sub-asset classes from constants) plus instruments from the
 * user's library and master catalog.
 *
 * Features:
 * - Multi-select destination picker for batch copy
 * - Target Type dropdown to filter by destination level
 * - Search input for breadcrumb/label filtering
 * - Card-based rows: breadcrumb header, bold label body, text-label footer
 * - Footer shows Type / Source (instruments only) / Fee Status
 * - Status colors: success-600 (new), warning-600 (has fees), default-500 (archived)
 * - Two-step overwrite confirmation with batch summary (N new, M overwrite)
 * - Per-destination batch copy orchestration with partial success handling
 * - Catalog instrument import on submit (same pattern as AddInstrumentModal)
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback } from 'react';

// 2. Workspace packages
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Input,
  Alert,
  Listbox,
  ListboxItem,
  Spinner,
  useToastNotification,
} from '@enfomentis/ui';
import { Search } from 'lucide-react';

// 3. Internal imports
import type { Instrument } from '@/types/schema/Instrument';
import type { BrokerFeeConfiguration } from '@/types/schema/Broker';
import type { AssetClass, SubAssetClass } from '@/types/shared/AssetClass';
import {
  ASSET_CLASS_TYPES,
  ASSET_CLASS_TYPE_DISPLAY_MAP,
  MARKET_SEGMENT_DISPLAY_MAP,
  SUB_ASSET_CLASS_DISPLAY_MAP,
  VALID_MARKET_SEGMENTS_BY_ASSET_CLASS,
  VALID_SUB_ASSET_CLASSES_BY_ASSET_AND_SEGMENT,
} from '@/constants/assetClass';
import { INSTRUMENT_CATALOG } from '@/data/instrumentCatalog';
import { instrumentService } from '@/services/InstrumentService';
import { findConfigByPath, buildFeeConfigPath } from './feeTree';
import type { FeeTreeNode, FeeConfigPath } from './feeTree';
import { useBrokers } from './context';

// =============================================================================
// Types
// =============================================================================

type TargetType = 'all' | 'market-segment' | 'sub-asset-class' | 'instrument';

interface DestinationCandidate {
  key: string;
  type: 'market-segment' | 'sub-asset-class' | 'instrument';
  breadcrumb: string;
  label: string;
  path: FeeConfigPath;
  status: 'new' | 'has-fees' | 'archived';
  source?: 'library' | 'catalog';
  catalogEntryId?: string;
}

export interface CopyFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceNode: FeeTreeNode;
  feeConfigurations: BrokerFeeConfiguration[];
  onCopied: (nodeId: string) => void;
}

// =============================================================================
// Helpers
// =============================================================================

const TARGET_TYPE_OPTIONS: { key: TargetType; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'market-segment', label: 'Market Segment' },
  { key: 'sub-asset-class', label: 'Sub-Asset Class' },
  { key: 'instrument', label: 'Instrument' },
];

const STATUS_LABELS: Record<DestinationCandidate['status'], string> = {
  'new': 'New',
  'has-fees': 'Has fees',
  'archived': 'Archived',
};

const STATUS_TEXT_COLORS: Record<DestinationCandidate['status'], string> = {
  'new': 'text-success-600',
  'has-fees': 'text-warning-600',
  'archived': 'text-default-500',
};

const TYPE_CHIP_LABELS: Record<DestinationCandidate['type'], string> = {
  'market-segment': 'Market Segment',
  'sub-asset-class': 'Sub-Asset Class',
  'instrument': 'Instrument',
};

/**
 * Build breadcrumb string from AssetClass classification using display maps
 */
function buildClassificationBreadcrumb(classification: AssetClass): string {
  const parts: string[] = [
    ASSET_CLASS_TYPE_DISPLAY_MAP[classification.assetClass] ?? classification.assetClass,
    MARKET_SEGMENT_DISPLAY_MAP[classification.marketSegment] ?? classification.marketSegment,
  ];
  if (classification.subAssetClass) {
    parts.push(SUB_ASSET_CLASS_DISPLAY_MAP[classification.subAssetClass] ?? classification.subAssetClass);
  }
  return parts.join(' > ');
}

/**
 * Determine config status at a given path
 */
function getConfigStatus(
  feeConfigurations: BrokerFeeConfiguration[],
  category: AssetClass | undefined,
  instrumentId: string | undefined
): DestinationCandidate['status'] {
  const existing = findConfigByPath(feeConfigurations, category, instrumentId);
  if (!existing) return 'new';
  if (existing.status === 'archived') return 'archived';
  return 'has-fees';
}

/**
 * Semantic comparison of two FeeConfigPaths
 */
function pathsMatch(a: FeeConfigPath, b: FeeConfigPath): boolean {
  const catA = a.category;
  const catB = b.category;
  if (catA && catB) {
    if (catA.assetClass !== catB.assetClass) return false;
    if (catA.marketSegment !== catB.marketSegment) return false;
    if (catA.subAssetClass !== catB.subAssetClass) return false;
  } else if (catA || catB) {
    return false;
  }
  return a.instrumentId === b.instrumentId;
}

/**
 * Stringify a FeeConfigPath for dedup tracking
 */
function pathKey(p: FeeConfigPath): string {
  const cat = p.category;
  const catStr = cat
    ? `${cat.assetClass}/${cat.marketSegment}/${cat.subAssetClass ?? ''}`
    : '';
  return `${catStr}|${p.instrumentId ?? ''}`;
}

/**
 * Build source breadcrumb from a FeeTreeNode for the header
 */
function buildSourceBreadcrumb(node: FeeTreeNode): string {
  const parts = node.categoryPath.map((segment, i) => {
    switch (i) {
      case 0:
        return ASSET_CLASS_TYPE_DISPLAY_MAP[segment as keyof typeof ASSET_CLASS_TYPE_DISPLAY_MAP] ?? segment;
      case 1:
        return MARKET_SEGMENT_DISPLAY_MAP[segment as keyof typeof MARKET_SEGMENT_DISPLAY_MAP] ?? segment;
      case 2:
        return SUB_ASSET_CLASS_DISPLAY_MAP[segment as keyof typeof SUB_ASSET_CLASS_DISPLAY_MAP] ?? segment;
      default:
        return segment;
    }
  });
  if (node.type === 'instrument') {
    parts.push(node.label);
  }
  return parts.join(' > ');
}

// =============================================================================
// Component
// =============================================================================

export const CopyFeeModal: React.FC<CopyFeeModalProps> = ({
  isOpen,
  onClose,
  sourceNode,
  feeConfigurations,
  onCopied,
}) => {
  // ---------------------------------------------------------------------------
  // Context & Hooks
  // ---------------------------------------------------------------------------

  const { copyFeeConfiguration, overwriteFeeConfiguration } = useBrokers();
  const { showToast } = useToastNotification();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [targetType, setTargetType] = useState<TargetType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set<string>());
  const [confirmedOverwrites, setConfirmedOverwrites] = useState<Set<string>>(new Set<string>());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Load Instruments & Reset State
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (isOpen) {
      // Reset all state
      setTargetType('all');
      setSearchQuery('');
      setSelectedKeys(new Set<string>());
      setConfirmedOverwrites(new Set<string>());
      setIsSubmitting(false);
      setSubmitError(null);

      // Load user library instruments
      setIsLoading(true);
      instrumentService
        .getAll()
        .then((result) => {
          setInstruments(result);
        })
        .catch((error) => {
          console.error('Failed to load instruments:', error);
          setInstruments([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Clear instruments when modal closes to free memory
      setInstruments([]);
    }
  }, [isOpen]);

  // Prune confirmed overwrites when selections change
  useEffect(() => {
    setConfirmedOverwrites((prev) => {
      const next = new Set<string>();
      for (const key of prev) {
        if (selectedKeys.has(key)) next.add(key);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [selectedKeys]);

  // ---------------------------------------------------------------------------
  // Source Path (for exclusion from destination list)
  // ---------------------------------------------------------------------------

  const sourcePath = useMemo(
    () => buildFeeConfigPath(sourceNode),
    [sourceNode]
  );

  // ---------------------------------------------------------------------------
  // Category Candidates
  // ---------------------------------------------------------------------------

  const categoryCandidates = useMemo((): DestinationCandidate[] => {
    const candidates: DestinationCandidate[] = [];

    for (const ac of ASSET_CLASS_TYPES) {
      const validSegments = VALID_MARKET_SEGMENTS_BY_ASSET_CLASS[ac];
      const segmentMap = VALID_SUB_ASSET_CLASSES_BY_ASSET_AND_SEGMENT[ac];

      // Market segment candidates
      for (const ms of validSegments) {
        const category: AssetClass = { assetClass: ac, marketSegment: ms };
        candidates.push({
          key: `ms:${ac}/${ms}`,
          type: 'market-segment',
          breadcrumb: ASSET_CLASS_TYPE_DISPLAY_MAP[ac],
          label: MARKET_SEGMENT_DISPLAY_MAP[ms],
          path: { category },
          status: getConfigStatus(feeConfigurations, category, undefined),
        });

        // Sub-asset class candidates (per-segment filtering)
        const segmentSubs = (segmentMap as Record<string, readonly SubAssetClass[] | undefined>)[ms];
        if (segmentSubs) {
          for (const sac of segmentSubs) {
            const subCategory: AssetClass = { assetClass: ac, marketSegment: ms, subAssetClass: sac };
            candidates.push({
              key: `sac:${ac}/${ms}/${sac}`,
              type: 'sub-asset-class',
              breadcrumb: `${ASSET_CLASS_TYPE_DISPLAY_MAP[ac]} > ${MARKET_SEGMENT_DISPLAY_MAP[ms]}`,
              label: SUB_ASSET_CLASS_DISPLAY_MAP[sac],
              path: { category: subCategory },
              status: getConfigStatus(feeConfigurations, subCategory, undefined),
            });
          }
        }
      }
    }

    return candidates;
  }, [feeConfigurations]);

  // ---------------------------------------------------------------------------
  // Instrument Candidates
  // ---------------------------------------------------------------------------

  // Set of catalog IDs already imported to user library
  const libraryReferenceIds = useMemo(() => {
    const ids = new Set<string>();
    for (const inst of instruments) {
      if (inst.instrumentReferenceId) {
        ids.add(inst.instrumentReferenceId);
      }
    }
    return ids;
  }, [instruments]);

  const instrumentCandidates = useMemo((): DestinationCandidate[] => {
    const candidates: DestinationCandidate[] = [];

    // Library instruments
    for (const inst of instruments) {
      if (inst.status !== 'active') continue;

      candidates.push({
        key: `lib:${inst.id}`,
        type: 'instrument',
        breadcrumb: buildClassificationBreadcrumb(inst.classification),
        label: `${inst.symbol} — ${inst.displayName}`,
        path: { category: inst.classification, instrumentId: inst.symbol },
        status: getConfigStatus(feeConfigurations, inst.classification, inst.symbol),
        source: 'library',
      });
    }

    // Catalog instruments (not already imported)
    for (const entry of INSTRUMENT_CATALOG) {
      if (libraryReferenceIds.has(entry.id)) continue;

      candidates.push({
        key: `cat:${entry.id}`,
        type: 'instrument',
        breadcrumb: buildClassificationBreadcrumb(entry.classification),
        label: `${entry.symbol} — ${entry.name}`,
        path: { category: entry.classification, instrumentId: entry.symbol },
        status: getConfigStatus(feeConfigurations, entry.classification, entry.symbol),
        source: 'catalog',
        catalogEntryId: entry.id,
      });
    }

    return candidates;
  }, [instruments, libraryReferenceIds, feeConfigurations]);

  // ---------------------------------------------------------------------------
  // Combined & Filtered Candidates
  // ---------------------------------------------------------------------------

  const allCandidates = useMemo((): DestinationCandidate[] => {
    const combined = [...categoryCandidates, ...instrumentCandidates];

    // Path-based source exclusion + dedup by semantic destination path
    const seen = new Set<string>();
    return combined.filter((c) => {
      // Exclude source node's own path
      if (sourcePath && pathsMatch(c.path, sourcePath)) return false;
      // Deduplicate by semantic destination path (e.g., two instruments with same classification+symbol)
      const pk = pathKey(c.path);
      if (seen.has(pk)) return false;
      seen.add(pk);
      return true;
    });
  }, [categoryCandidates, instrumentCandidates, sourcePath]);

  const typedCandidates = useMemo((): DestinationCandidate[] => {
    if (targetType === 'all') return allCandidates;
    return allCandidates.filter((c) => c.type === targetType);
  }, [allCandidates, targetType]);

  const filteredCandidates = useMemo((): DestinationCandidate[] => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return typedCandidates;
    return typedCandidates.filter(
      (c) => `${c.breadcrumb} ${c.label}`.toLowerCase().includes(query)
    );
  }, [typedCandidates, searchQuery]);

  // Remove selections no longer visible in filtered results
  useEffect(() => {
    if (selectedKeys.size === 0) return;
    const visibleKeys = new Set(filteredCandidates.map((c) => c.key));
    const nextKeys = new Set<string>();
    for (const key of selectedKeys) {
      if (visibleKeys.has(key)) nextKeys.add(key);
    }
    if (nextKeys.size !== selectedKeys.size) {
      setSelectedKeys(nextKeys);
    }
  }, [filteredCandidates, selectedKeys]);

  // ---------------------------------------------------------------------------
  // Selection & Overwrite (Multi-Select)
  // ---------------------------------------------------------------------------

  const selectedCandidates = useMemo(
    () => allCandidates.filter((c) => selectedKeys.has(c.key)),
    [allCandidates, selectedKeys]
  );

  const overwriteCandidates = useMemo(
    () => selectedCandidates.filter((c) => c.status === 'has-fees'),
    [selectedCandidates]
  );

  const hasUnconfirmedOverwrites = overwriteCandidates.some(
    (c) => !confirmedOverwrites.has(c.key)
  );

  const allOverwritesConfirmed =
    overwriteCandidates.length > 0 && !hasUnconfirmedOverwrites;

  // ---------------------------------------------------------------------------
  // Selection Handler
  // ---------------------------------------------------------------------------

  const handleSelectionChange = useCallback(
    (keys: 'all' | Set<React.Key>): void => {
      if (keys === 'all') {
        // Select all currently filtered candidates
        setSelectedKeys(new Set(filteredCandidates.map((c) => c.key)));
      } else {
        setSelectedKeys(new Set(Array.from(keys) as string[]));
      }
      setSubmitError(null);
    },
    [filteredCandidates]
  );

  // ---------------------------------------------------------------------------
  // Target Type Handler
  // ---------------------------------------------------------------------------

  const handleTargetTypeChange = useCallback(
    (keys: 'all' | Set<React.Key>): void => {
      if (keys === 'all') return;
      const rawKey = Array.from(keys)[0] as string | undefined;
      if (rawKey) {
        setTargetType(rawKey as TargetType);
      }
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Submit (Batch)
  // ---------------------------------------------------------------------------

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (selectedCandidates.length === 0) return;

    // Step 1: If unconfirmed overwrites exist, confirm them instead of submitting
    if (hasUnconfirmedOverwrites) {
      setSubmitError(null);
      setConfirmedOverwrites(new Set(overwriteCandidates.map((c) => c.key)));
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    const results: Array<{ key: string; label: string; success: boolean; nodeId?: string; error?: string }> = [];

    try {
      const sourceConfigId = sourceNode.feeConfigIds[0];
      if (!sourceConfigId) {
        setSubmitError('Source node has no fee configuration.');
        return;
      }

      for (const candidate of selectedCandidates) {
        try {
          let resolvedPath: FeeConfigPath = { ...candidate.path };

          // Catalog import step: import to library first, then use symbol as instrumentId
          if (candidate.source === 'catalog' && candidate.catalogEntryId) {
            const importResult = await instrumentService.importFromCatalog([candidate.catalogEntryId]);

            const created = importResult.created[0];
            const duplicate = importResult.duplicates[0];

            if (created) {
              resolvedPath = { category: created.classification, instrumentId: created.symbol };
            } else if (duplicate) {
              resolvedPath = {
                category: duplicate.existingInstrument.classification,
                instrumentId: duplicate.existingInstrument.symbol,
              };
            } else {
              const errorMsg = importResult.errors[0]?.error ?? 'Unknown import error';
              results.push({ key: candidate.key, label: candidate.label, success: false, error: `Import failed: ${errorMsg}` });
              continue;
            }
          }

          const params = { sourceConfigId, destination: resolvedPath };
          const isOverwrite = candidate.status === 'has-fees';

          if (isOverwrite) {
            const result = await overwriteFeeConfiguration(params);
            if (result.success) {
              results.push({ key: candidate.key, label: candidate.label, success: true, nodeId: result.nodeId });
            } else {
              results.push({ key: candidate.key, label: candidate.label, success: false, error: result.reason });
            }
          } else {
            const result = await copyFeeConfiguration(params);
            if (result.success) {
              results.push({ key: candidate.key, label: candidate.label, success: true, nodeId: result.nodeId });
            } else if (result.reason === 'active_exists') {
              // Race condition: destination gained fees since modal opened — don't auto-overwrite
              results.push({
                key: candidate.key,
                label: candidate.label,
                success: false,
                error: 'Destination now has fees — re-open to confirm overwrite',
              });
            } else {
              results.push({ key: candidate.key, label: candidate.label, success: false, error: result.reason });
            }
          }
        } catch (error) {
          console.error(`Failed to copy to ${candidate.label}:`, error);
          results.push({ key: candidate.key, label: candidate.label, success: false, error: 'Unexpected error' });
        }
      }

      const succeeded = results.filter((r) => r.success);
      const failed = results.filter((r) => !r.success);
      const firstNodeId = succeeded.find((r) => r.nodeId)?.nodeId;

      if (succeeded.length === results.length) {
        // All succeeded
        showToast({
          title: 'Fees copied',
          description: `Fee configuration copied to ${succeeded.length} destination${succeeded.length > 1 ? 's' : ''}.`,
          color: 'success',
        });
        if (firstNodeId) onCopied(firstNodeId);
        onClose();
      } else if (succeeded.length > 0) {
        // Partial success
        showToast({
          title: 'Fees partially copied',
          description: `Copied to ${succeeded.length} of ${results.length} destinations. ${failed.length} failed.`,
          color: 'warning',
        });
        if (firstNodeId) onCopied(firstNodeId);
        onClose();
      } else {
        // All failed
        setSubmitError(`All ${failed.length} copy operations failed. ${failed[0]?.error ?? 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to batch copy fee configuration:', error);
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedCandidates,
    hasUnconfirmedOverwrites,
    overwriteCandidates,
    sourceNode.feeConfigIds,
    copyFeeConfiguration,
    overwriteFeeConfiguration,
    showToast,
    onCopied,
    onClose,
  ]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const sourceBreadcrumb = useMemo(
    () => buildSourceBreadcrumb(sourceNode),
    [sourceNode]
  );

  const isFormValid = selectedCandidates.length > 0 && !isSubmitting;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      placement="center"
      isDismissable={!isSubmitting}
      hideCloseButton={isSubmitting}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <span>Copy Fees</span>
          <span className="text-sm font-normal text-text-tertiary">
            From: {sourceBreadcrumb}
          </span>
        </ModalHeader>

        <ModalBody className="gap-4">
          {/* Controls Row */}
          <div className="flex gap-2">
            <Select
              label="Target Type"
              size="sm"
              selectedKeys={[targetType]}
              onSelectionChange={handleTargetTypeChange}
              className="w-48"
              isDisabled={isSubmitting}
              labelPlacement="outside-top"
            >
              {TARGET_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.key}>{opt.label}</SelectItem>
              ))}
            </Select>
            <Input
              placeholder="Search destinations..."
              size="sm"
              value={searchQuery}
              onValueChange={setSearchQuery}
              startContent={<Search className="w-4 h-4 text-text-secondary" />}
              isClearable
              className="flex-1"
              isDisabled={isSubmitting}
              label="Search"
              labelPlacement="outside-top"
            />
          </div>

          {/* Destination List */}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : filteredCandidates.length === 0 ? (
            <p className="text-text-tertiary text-sm text-center py-6">
              {allCandidates.length === 0
                ? 'No destinations available.'
                : searchQuery.trim()
                  ? 'No destinations match your search.'
                  : 'No destinations match the selected type.'}
            </p>
          ) : (
              <Listbox
                aria-label="Select destinations"
                selectionMode="multiple"
                selectedKeys={selectedKeys}
                onSelectionChange={handleSelectionChange}
                className="max-h-100 overflow-y-auto border border-default-200"
              >
                {filteredCandidates.map((candidate) => (
                  <ListboxItem
                    key={candidate.key}
                    textValue={`${candidate.breadcrumb} ${candidate.label}`}
                    className="border-1 border-default-200"
                  >
                    <Card shadow="none" radius="none" className="w-full border-none bg-transparent">
                      <CardHeader className="pb-0 pt-1 px-1 text-xs text-text-tertiary">
                        {candidate.breadcrumb}
                      </CardHeader>
                      <CardBody className="py-0 px-1">
                        <span className="font-semibold">{candidate.label}</span>
                      </CardBody>
                      <CardFooter className="pt-0 pb-1 px-1 text-xs text-text-secondary">
                        <span>
                          Type: {TYPE_CHIP_LABELS[candidate.type]}
                          {candidate.source && ` | Source: ${candidate.source === 'library' ? 'Library' : 'Catalog'}`}
                          {' | Fee Status: '}
                          <span className={STATUS_TEXT_COLORS[candidate.status]}>{STATUS_LABELS[candidate.status]}</span>
                        </span>
                      </CardFooter>
                    </Card>
                  </ListboxItem>
                ))}
              </Listbox>
          )}

          {/* Batch Overwrite Warning */}
          {overwriteCandidates.length > 0 && selectedCandidates.length > 0 && (
            <Alert color="warning" variant="flat">
              <strong>
                Copying to {selectedCandidates.length} destination{selectedCandidates.length > 1 ? 's' : ''}:
              </strong>{' '}
              {selectedCandidates.length - overwriteCandidates.length} new
              {overwriteCandidates.length > 0 && (
                <>, {overwriteCandidates.length} will be overwritten
                  ({overwriteCandidates.map((c) => `${c.breadcrumb} > ${c.label}`).join(', ')})
                </>
              )}
            </Alert>
          )}

          {/* Error Alert */}
          {submitError && (
            <Alert color="danger" variant="flat">
              {submitError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color={allOverwritesConfirmed ? 'warning' : 'primary'}
            onPress={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!isFormValid}
          >
            {allOverwritesConfirmed
              ? `Overwrite & Copy to ${selectedCandidates.length}`
              : `Copy to ${selectedCandidates.length}`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CopyFeeModal;
